import type {
  QueueEntry,
  QueueStorage,
  SyncStatus,
  Transport,
  TurnPayload,
} from "./types.js";

/**
 * Durable outbox for turns.
 *
 * Two rules carry the whole design:
 *
 *  1. Writes are local first, always. `enqueue` never awaits the network, so
 *     scoring a turn is instant whether or not there is signal, and the game is
 *     playable start to finish offline.
 *
 *  2. A flush sends strictly in order and stops at the first retryable failure.
 *     The server rejects an out-of-order `seq` with 409, so skipping a stuck
 *     entry to "make progress" would corrupt the log rather than repair it.
 *
 * A permanently rejected entry (422 -- a bank below 350, say) is the one case
 * where the queue does move past an entry, because retrying can never change
 * the answer. It is marked `rejected` and surfaced, never silently dropped:
 * somebody's score is missing and they have to be told.
 */

export class SyncQueue {
  #entries: QueueEntry[];
  readonly #storage: QueueStorage;
  readonly #transport: Transport;
  #syncing = false;
  #lastError: string | undefined;
  #listeners = new Set<(status: SyncStatus) => void>();
  /** Serialises flushes: two in parallel would race on ordering. */
  #inFlight: Promise<void> | null = null;

  constructor(storage: QueueStorage, transport: Transport) {
    this.#storage = storage;
    this.#transport = transport;
    this.#entries = storage.read();
  }

  /** Record a turn locally and return at once. The network comes later. */
  enqueue(gameId: string, payload: TurnPayload): void {
    // The idempotency key also de-duplicates locally: a double tap on the same
    // button must not queue the same turn twice.
    const already = this.#entries.some(
      (e) => e.gameId === gameId && e.payload.clientId === payload.clientId,
    );
    if (already) return;

    this.#entries.push({ gameId, payload, state: "pending" });
    this.#persist();
  }

  /** Entries the server has not accepted yet, in send order. */
  pending(): QueueEntry[] {
    return this.#entries.filter((e) => e.state === "pending");
  }

  rejected(): QueueEntry[] {
    return this.#entries.filter((e) => e.state === "rejected");
  }

  status(): SyncStatus {
    return {
      online: this.#online(),
      syncing: this.#syncing,
      pending: this.pending().length,
      rejected: this.rejected().length,
      ...(this.#lastError !== undefined ? { lastError: this.#lastError } : {}),
    };
  }

  /** Subscribe to status changes; returns an unsubscribe function. */
  subscribe(listener: (status: SyncStatus) => void): () => void {
    this.#listeners.add(listener);
    listener(this.status());
    return () => this.#listeners.delete(listener);
  }

  /**
   * Send everything pending. Safe to call at any time -- concurrent calls share
   * one in-flight pass rather than racing each other through the queue.
   */
  async flush(): Promise<void> {
    if (this.#inFlight) return this.#inFlight;
    this.#inFlight = this.#run().finally(() => {
      this.#inFlight = null;
    });
    return this.#inFlight;
  }

  async #run(): Promise<void> {
    if (!this.#online()) {
      this.#lastError = "offline";
      this.#emit();
      return;
    }
    this.#syncing = true;
    this.#emit();
    try {
      // Re-read `pending()` each iteration: enqueue may append while we send.
      for (;;) {
        const next = this.pending()[0];
        if (!next) break;

        const result = await this.#transport(next.gameId, next.payload);
        if (result.ok) {
          next.state = "synced";
          this.#lastError = undefined;
          this.#persist();
          continue;
        }
        if ("rebase" in result) {
          // Another device got there first. Renumber this game's pending turns
          // from where the server actually is and try again. The client id is
          // unchanged, so a turn the server already has stays de-duplicated.
          if (!this.#rebase(next.gameId, result.rebase)) {
            this.#lastError = result.error;
            break;
          }
          continue;
        }
        if (result.permanent) {
          // Retrying cannot change the answer, so step past it -- but keep it
          // visible. A dropped turn is a missing score, not a tidy queue.
          next.state = "rejected";
          next.error = result.error;
          this.#persist();
          continue;
        }
        // Retryable: stop here. Sending later entries now would open a seq gap.
        this.#lastError = result.error;
        break;
      }
    } finally {
      this.#syncing = false;
      this.#emit();
    }
  }

  /**
   * Repoint queued turns at the id the server gave this game.
   *
   * A game can be started with no network at all, so it exists locally long
   * before the server has heard of it and its turns are queued against a
   * locally-generated id. Once the game is created server-side -- idempotently,
   * keyed on the same client id -- its turns have to be re-addressed, or they
   * would be posted to a game that does not exist and rejected as 404.
   */
  remapGame(from: string, to: string): number {
    if (from === to) return 0;
    let moved = 0;
    for (const entry of this.#entries) {
      // Synced entries are history: rewriting them would be a lie about where
      // they went, and they are never sent again anyway.
      if (entry.state !== "pending" || entry.gameId !== from) continue;
      entry.gameId = to;
      moved++;
    }
    if (moved > 0) this.#persist();
    return moved;
  }

  /** Forget entries the server has accepted. Rejected ones stay until dismissed. */
  prune(): void {
    this.#entries = this.#entries.filter((e) => e.state !== "synced");
    this.#persist();
  }

  /** Acknowledge a rejection the user has seen. */
  dismiss(clientId: string): void {
    this.#entries = this.#entries.filter(
      (e) => !(e.state === "rejected" && e.payload.clientId === clientId),
    );
    this.#persist();
  }

  /**
   * Put every rejected entry back in the queue to try again.
   *
   * A rejection is not always the client's fault: a turn posted to a game the
   * server had not been told about yet came back 404 and was rejected, when it
   * only needed the game to be created first. Once that is fixed, retrying lets
   * the pile drain instead of forcing the user to discard real scores.
   */
  retryRejected(): number {
    let moved = 0;
    for (const entry of this.#entries) {
      if (entry.state === "rejected") {
        entry.state = "pending";
        delete entry.error;
        moved++;
      }
    }
    if (moved > 0) this.#persist();
    return moved;
  }

  /** Discard every rejected entry. The scores in them are given up for good. */
  clearRejected(): number {
    const before = this.#entries.length;
    this.#entries = this.#entries.filter((e) => e.state !== "rejected");
    const removed = before - this.#entries.length;
    if (removed > 0) this.#persist();
    return removed;
  }

  /**
   * Renumber a game's pending turns from `nextSeq`, preserving their order.
   * Returns false when that would change nothing -- the server is telling us to
   * move to where we already are, and retrying would spin forever.
   */
  #rebase(gameId: string, nextSeq: number): boolean {
    const pending = this.#entries.filter((e) => e.state === "pending" && e.gameId === gameId);
    const first = pending[0];
    if (!first || first.payload.seq === nextSeq) return false;

    pending.forEach((entry, index) => {
      entry.payload.seq = nextSeq + index;
    });
    this.#persist();
    return true;
  }

  #online(): boolean {
    // `navigator.onLine` only ever proves the negative: false means definitely
    // offline, true means "there is an interface", not "the server answers".
    // Treating a missing navigator as online keeps this usable under Node.
    return typeof navigator === "undefined" || navigator.onLine !== false;
  }

  #persist(): void {
    this.#storage.write(this.#entries);
    this.#emit();
  }

  #emit(): void {
    const status = this.status();
    for (const listener of this.#listeners) listener(status);
  }
}

/** localStorage-backed storage. Falls back to memory if it is unavailable. */
export function localQueueStorage(key = "kniffel.outbox"): QueueStorage {
  return {
    read(): QueueEntry[] {
      try {
        const raw = globalThis.localStorage?.getItem(key);
        if (!raw) return [];
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as QueueEntry[]) : [];
      } catch {
        // A corrupt outbox must not make the app unstartable.
        return [];
      }
    },
    write(entries: QueueEntry[]): void {
      try {
        globalThis.localStorage?.setItem(key, JSON.stringify(entries));
      } catch {
        // Quota or private mode: the in-memory queue still works for this
        // session, which is better than refusing to record the turn.
      }
    },
  };
}
